import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Clock, CheckCircle, Truck, XCircle, RefreshCw,
  CreditCard, Banknote, Smartphone,
} from "lucide-react";

const STATUS_CFG = {
  pending:    { label: "Pendente",    color: "bg-yellow-100 text-yellow-700", Icon: Clock },
  confirmed:  { label: "Confirmado",  color: "bg-blue-100 text-blue-700",     Icon: CheckCircle },
  preparing:  { label: "Preparando",  color: "bg-orange-100 text-orange-700", Icon: Clock },
  ready:      { label: "Pronto",      color: "bg-purple-100 text-purple-700", Icon: CheckCircle },
  delivering: { label: "Em Entrega",  color: "bg-indigo-100 text-indigo-700", Icon: Truck },
  delivered:  { label: "Entregue",    color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle },
  cancelled:  { label: "Cancelado",   color: "bg-red-100 text-red-700",       Icon: XCircle },
};

const PAY_ICONS = {
  card:   <CreditCard className="w-3.5 h-3.5" />,
  viva:   <CreditCard className="w-3.5 h-3.5" />,
  online: <CreditCard className="w-3.5 h-3.5" />,
  cash:   <Banknote className="w-3.5 h-3.5" />,
  mbway:  <Smartphone className="w-3.5 h-3.5" />,
};

const PAY_LABELS = { card: "Tarjeta", viva: "Viva Wallet", online: "Online", cash: "Efectivo", mbway: "MB Way" };

const TYPE_LABEL = { delivery: "🛵 Entrega", takeaway: "🥡 Recogida", table: "🍽️ Mesa", dine_in: "🍽️ Mesa" };

function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function deliveryMins(order) {
  if (!order.created_at || !order.delivered_at) return null;
  const mins = Math.round((new Date(order.delivered_at) - new Date(order.created_at)) / 60000);
  return mins > 0 && mins < 180 ? mins : null;
}

export function PortalOrderCard({ order, onReorder, onTrack, showFinancial = true }) {
  const cfg = STATUS_CFG[order.status] || STATUS_CFG.pending;
  const { Icon } = cfg;
  const mins     = deliveryMins(order);
  const savings  = order.discount_amount || order.coupon_discount || 0;
  const earned   = order.cashback_earned || 0;
  const used     = order.cashback_used   || 0;
  const payKey   = order.payment_method;
  const payIcon  = payKey ? PAY_ICONS[payKey] : null;
  const payLabel = payKey ? PAY_LABELS[payKey] || payKey : null;

  return (
    <Card className="border-0 shadow-sm ring-1 ring-gray-100">
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <Badge className={`${cfg.color} gap-1 text-xs`}><Icon className="w-3 h-3" />{cfg.label}</Badge>
              {order.order_type && (
                <Badge variant="outline" className="text-xs border-gray-200 text-gray-500">
                  {TYPE_LABEL[order.order_type] || order.order_type}
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-400">{fmtDate(order.created_at)}</p>
          </div>
          <p className="font-black text-xl text-gray-900 shrink-0">€{(order.total || 0).toFixed(2)}</p>
        </div>

        {/* Items */}
        {order.items?.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-2.5 mb-3 space-y-1">
            {order.items.slice(0, 5).map((item, i) => (
              <div key={i} className="flex justify-between items-center text-xs text-gray-600">
                <span className="truncate">
                  {(item.quantity || 1) > 1 && <span className="font-semibold mr-1">{item.quantity}×</span>}
                  {item.name || item.product_name}
                </span>
                {item.price != null && (
                  <span className="text-gray-400 ml-2 shrink-0">
                    €{((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                  </span>
                )}
              </div>
            ))}
            {order.items.length > 5 && (
              <p className="text-xs text-gray-400 mt-0.5">+{order.items.length - 5} itens</p>
            )}
          </div>
        )}

        {/* Meta details row */}
        {showFinancial && (mins || payLabel || savings > 0 || earned > 0 || used > 0) && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
            {mins && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />{mins} min
              </span>
            )}
            {payIcon && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                {payIcon}{payLabel}
              </span>
            )}
            {savings > 0 && (
              <span className="text-xs text-emerald-600 font-semibold">-€{savings.toFixed(2)} desconto</span>
            )}
            {earned > 0 && (
              <span className="text-xs text-amber-600 font-semibold">+€{earned.toFixed(2)} cashback</span>
            )}
            {used > 0 && (
              <span className="text-xs text-purple-600 font-semibold">€{used.toFixed(2)} cashback usado</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {order.items?.length > 0 && (
            <Button
              size="sm"
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1.5 font-semibold"
              onClick={() => onReorder(order)}
            >
              <RefreshCw className="w-3.5 h-3.5" />Pedir novamente
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onTrack(order.id)}>
            Ver detalhes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
